import React from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import InlineSVG from 'svg-inline-react';

import { isFarmEqual } from 'modules/utils/saleCategoriesAndFilters';
import { PRODUCTS_LIST } from 'modules/originUtils.js';

import useI18n from 'hooks/I18n/useI18n';

import Heading from 'components/Heading.jsx';
import Text from 'components/ProductIdentity/Text.jsx';
import FarmHeader from 'components/Sale/ProductIdentity/FarmHeader.jsx';
import ProductCard, { MODE_LARGE } from 'components/Sale/ProductIdentity/ProductCard.jsx';
import LimitOverflowingContentWithEllipsis from 'components/ProductIdentity/LimitOverflowingContentWithEllipsis.jsx';
import ALMBanner from 'src/components/organisms/ALM/ALMBanner.jsx';

import PhoneCallIcon from 'app/assets/new-design/images/icons/phone-call.svg';
import BoxTutorialIcon from 'app/assets/new-design/images/illustrations/ALM/tutorial-box.svg';
import DeliveryTutorialIcon from 'app/assets/new-design/images/illustrations/ALM/tutorial-delivery.svg';
import ProducerTutorialIcon from 'app/assets/new-design/images/illustrations/ALM/tutorial-producer.svg';

const SaleContent = ({
    farms,
    products,
    addOfferToBasket,
    onClickOnProduct,
    distribution,
    assembly,
    productTypes,
}) => {
    const { trans } = useI18n();
    return (
        <div className="alm-sale-content">
            <ALMBanner onSalePage />
            {farms.map(farm => {
                const assemblyId = assembly.id;
                const farmProducts = R.filter(isFarmEqual(farm.id), products);
                return (
                    <>
                        <FarmHeader
                            title={farm.name}
                            image={farm.farmerPhotoId}
                            description={farm.description}
                            assemblyId={assemblyId}
                        />
                        <Heading
                            className="pi-products-list-section-title"
                            rank={3}
                            size={4}
                            bold
                            serif
                            productIdentity
                        >
                            {trans('salePage.allItsProducts')}
                        </Heading>
                        <div className="alm-products-list-cards">
                            {farmProducts.map(product => (
                                <ProductCard
                                    mode={MODE_LARGE}
                                    showProducer={false}
                                    addOfferToBasket={addOfferToBasket}
                                    allProductTypesIndexedById={productTypes}
                                    assemblyId={assembly.id}
                                    distributionId={distribution.id}
                                    farm={product.farm}
                                    product={product}
                                    isLabelDescriptionAvailable={false}
                                    actionOrigin={PRODUCTS_LIST}
                                    onClick={() => onClickOnProduct(product.id)}
                                />
                            ))}
                        </div>
                    </>
                );
            })}
            <Text size="24px" lineHeight="32px" weight={600} color="grey8">
                {trans('alm.tutorial.title')}
            </Text>
            <LimitOverflowingContentWithEllipsis
                reduceKey="global.show.less"
                expandKey="global.show.more"
            >
                {trans('alm.tutorial.description')}
            </LimitOverflowingContentWithEllipsis>
            <div className="tutorial-steps-contact">
                <div className="tutorial-steps">
                    <div>
                        <div className="tutorial-steps-illustration">
                            <InlineSVG src={BoxTutorialIcon} />
                        </div>
                        <Text weight={600} size="16px" lineHeight="24px" color="grey8">
                            {trans('alm.tutorial.step1.title')}
                        </Text>
                        <Text weight={400} size="16px" lineHeight="24px" color="gray6">
                            {trans('alm.tutorial.step1.description')}
                        </Text>
                    </div>
                    <div>
                        <div className="tutorial-steps-illustration">
                            <InlineSVG src={ProducerTutorialIcon} />
                        </div>
                        <Text weight={600} size="16px" lineHeight="24px" color="grey8">
                            {trans('alm.tutorial.step2.title')}
                        </Text>
                        <Text weight={400} size="16px" lineHeight="24px" color="gray6">
                            {trans('alm.tutorial.step2.description')}
                        </Text>
                    </div>
                    <div>
                        <div className="tutorial-steps-illustration">
                            <InlineSVG src={DeliveryTutorialIcon} />
                        </div>
                        <Text weight={600} size="16px" lineHeight="24px" color="grey8">
                            {trans('alm.tutorial.step3.title')}
                        </Text>
                        <Text weight={400} size="16px" lineHeight="24px" color="gray6">
                            {trans('alm.tutorial.step3.description')}
                        </Text>
                    </div>
                </div>
                <div>
                    <Text weight={600} size="16px" lineHeight="24px" color="grey8">
                        <InlineSVG raw src={PhoneCallIcon} />
                        {trans('alm.tutorial.contact.title')}
                    </Text>
                    <div className="alm-tutorial-contacts-list">
                        <Text size="14px" lineHeight="20px" color="turquoise2">
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                className="nd-link"
                                href={`mailto:${trans('alm.tutorial.contact.mail')}`}
                            >
                                {trans('alm.tutorial.contact.mail')}
                            </a>
                        </Text>
                        <Text size="14px" lineHeight="20px" color="turquoise2">
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                className="nd-link"
                                href={`tel:${trans('alm.tutorial.contact.phone')}`}
                            >
                                {trans('alm.tutorial.contact.phone')}
                            </a>
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

SaleContent.propTypes = {
    farms: PropTypes.array.isRequired,
    products: PropTypes.array.isRequired,
    addOfferToBasket: PropTypes.func.isRequired,
    onClickOnProduct: PropTypes.func.isRequired,
    distribution: PropTypes.shape({ id: PropTypes.number }),
    assembly: PropTypes.shape({
        id: PropTypes.number,
        place: PropTypes.shape({
            address: PropTypes.shape({
                coordinates: PropTypes.object,
                country: PropTypes.shape({ code: PropTypes.string }),
            }),
        }),
    }),
    productTypes: PropTypes.object.isRequired,
};
export default SaleContent;
